import { Component, ChangeDetectionStrategy } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { ClaimsResponse } from '../../login/objects/claimsResponse';
import { environment } from '../../environments/environment';
import { SessionService } from '../../services/session.service';
import { KnownRole } from '../../models/known-role.enum';


@Component({
	standalone: true,
	selector: 'navi',
	imports: [NgbModule, RouterModule],
	templateUrl: './navi.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './navi.component.scss'
})
export class NaviComponent {
	constructor(
		public userService: UserService,
		private sessionService: SessionService,
		private router: Router,
	) { }

	get searchLocationUrl(): string {
		return environment.popUrl + 'Search';
	}
	get orderHistoryUrl(): string {
		return environment.popUrl + 'History/Search';
	}

	get returnsUrl(): string {
		return environment.popUrl + 'Rma/List';
	}

	get shippedOrdersReportUrl(): string {
		return environment.popUrl + 'Reports/ShipReport';
	}

	get customerOrderUrl(): string {
		return environment.popUrl + 'Order/CreateOrder';
	}

	get trialsStockOrderUrl(): string {
		return environment.popUrl + 'StoreOrder/Create';
	}

	get accountDetailsUrl(): string {
		return environment.popUrl + 'PartnerAccount/PartnerAccountDetails';
	}

	get billingUrl(): string {
		return environment.popUrl + 'Billing/Search';
	}

	get affiliationUrl(): string {
		return environment.popUrl + 'Affiliation';
	}

	get manufacturerAdminUrl(): string {
		return environment.popUrl + 'ManufacturerAdmin';
	}

	get userInfo(): ClaimsResponse | null {
		return this.userService.userInfo;
	}

	get userName(): string | null {
		return this.userService.userName;
	}

	getClaim(claimName: string): string | null {
		return this.userService.getClaim(claimName);
	}

	get isInternalUser(): boolean {
		const hasUserRole = this.getClaim('roles') === 'User' || this.getClaim('roles') === 'Admin';
		const hasPartnerIdClaim = this.getClaim('partnerId');

		return hasUserRole && !hasPartnerIdClaim;
	}

	get isPartnerUser(): boolean {
		return this.getPartnerId() !== null;
	}

	hasPermission(permission: string): boolean {
		const combinedUserData = this.userService.currentCombinedUserData;
		return combinedUserData?.hasPermission(permission) || false;
	}

	private getPartnerId(): number | null {
		const partnerIdFromClaim = this.getClaim('partnerId');
		const combinedUserData = this.userService.currentCombinedUserData;
		const partnerIdFromUserData = combinedUserData?.user?.partnerId;

		if (partnerIdFromClaim) {
			return parseInt(partnerIdFromClaim, 10);
		}

		return partnerIdFromUserData !== undefined ? partnerIdFromUserData : null;
	}

	get isLuxottica(): boolean {
		return this.getPartnerId() === 1;
	}

	get isGusa(): boolean {
		return this.getPartnerId() === 10;
	}

	get isManufacturerUser(): boolean {
		return this.sessionService.isManufacturerUser();
	}

	get showSearchLocation(): boolean {
		return this.isInternalUser;
	}

	get showNewOrder(): boolean {
		return this.isInternalUser || (this.isPartnerUser && this.hasPermission('CreateOrder'));
	}

	get showReports(): boolean {
		// I did have this as "hasPermission "CanReadFinanceReports"" but the only link we have is shipping reports
		// which is internal only. so leaving it like this for now
		return this.isInternalUser;
	}

	get showManufacturerAdmin(): boolean {
		return this.isInternalUser;
	}

	get showBilling(): boolean {
		return this.isInternalUser || (this.isPartnerUser && this.hasPermission('ManageBilling'));
	}

	get showAffiliation(): boolean {
		return this.isInternalUser;
	}

	get showAccountDetails(): boolean {
		return !this.isLuxottica && !this.isGusa;
	}

	get showUserManagement(): boolean {
		return this.isInternalUser ||
			(this.isPartnerUser && this.hasPermission('ManageAccount')) ||
			(this.sessionService.isManufacturerUser() && this.hasPermission('ManageAccount'));
	}

	get showProductLookup(): boolean {
		return this.isInternalUser;
	}
	get showPayloadLookup(): boolean {
		return this.isInternalUser;
	}

	get showOrderVolume(): boolean {
		if (this.isPartnerUser || this.isManufacturerUser || this.sessionService.getManufacturerId()) {
			return false;
		}

		const roleId = Number(this.userService.currentCombinedUserData?.user?.roleID);
		if (roleId === KnownRole.SuperAdmin || roleId === KnownRole.InternalAdmin) {
			return true;
		}
		if (roleId) {
			return false;
		}

		return this.isInternalUser;
	}

	get showDashboards(): boolean {
		return this.showOrderVolume;
	}

	get showSignout(): boolean {
		const securityLevel = this.getClaim('securityLevel');
		return securityLevel === 'Associate' || this.isInternalUser || this.sessionService.isManufacturerUser();
	}

	signOut(): void {
		this.sessionService.clearSession();
		this.userService.setUserInfo(null);
		this.router.navigate(['/']);
	}
}

